import { useState, useEffect, useCallback } from 'react';

// Constant for storage key prefix
export const STORAGE_KEYS = {
    CREATE_PROJECT: 'create-project'
} as const;

type StorageKey = string;

interface SessionStorageHook<T> {
    value: T;
    setValue: (value: T | ((val: T) => T)) => void;
    remove: () => void;
    clear: () => void;
}

const useSessionStorage = <T,>(key: StorageKey, initialValue: T): SessionStorageHook<T> => {
    // Initialize state with value from session storage or initial value
    const [storedValue, setStoredValue] = useState<T>(() => {
        try {
            const item = sessionStorage.getItem(key);
            return item ? JSON.parse(item) : initialValue;
        } catch (error) {
            console.error('Error reading from sessionStorage:', error);
            return initialValue;
        }
    });

    // Update session storage when state changes
    useEffect(() => {
        try {
            sessionStorage.setItem(key, JSON.stringify(storedValue));
        } catch (error) {
            console.error('Error writing to sessionStorage:', error);
        }
    }, [storedValue, key]);

    // Remove item from session storage
    const remove = useCallback(() => {
        try {
            sessionStorage.removeItem(key);
            setStoredValue(initialValue);
        } catch (error) {
            console.error('Error removing from sessionStorage:', error);
        }
    }, [key, initialValue]);

    // Clear all items from session storage
    const clear = useCallback(() => {
        try {
            sessionStorage.clear();
            setStoredValue(initialValue);
        } catch (error) {
            console.error('Error clearing sessionStorage:', error);
        }
    }, [initialValue]);

    return {
        value: storedValue,
        setValue: setStoredValue,
        remove,
        clear
    };
};

// Utility function to get all items from session storage
export const getAllSessionStorageItems = (): Record<string, any> => {
    const items: Record<string, any> = {};
    try {
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key) {
                const value = sessionStorage.getItem(key);
                if (value) {
                    items[key] = JSON.parse(value);
                }
            }
        }
    } catch (error) {
        console.error('Error getting all items from sessionStorage:', error);
    }
    return items;
};

// Utility function to get items by prefix
export const getSessionStorageItemsByPrefix = (prefix: string): Record<string, any> => {
    const items: Record<string, any> = {};
    try {
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(prefix)) {
                const value = sessionStorage.getItem(key);
                if (value) {
                    items[key] = JSON.parse(value);
                }
            }
        }
    } catch (error) {
        console.error('Error getting items by prefix from sessionStorage:', error);
    }
    return items;
};

// Utility function to clear items by prefix
export const clearSessionStorageItemsByPrefix = (prefix: string): void => {
    try {
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(prefix)) {
                sessionStorage.removeItem(key);
            }
        }
    } catch (error) {
        console.error('Error clearing items by prefix from sessionStorage:', error);
    }
};

export default useSessionStorage;
